import { useState, useEffect } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Menu, X, Package2, FolderKanban, LayoutDashboard, ShoppingCart, BookOpen, Store, Mail, Heart, Settings, Sparkles, FileText, BarChart3, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const OWNER_EMAIL = 'piia.tarkiainen@veravalue.fi';

export const Navbar = () => {
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isOwner = user?.email === OWNER_EMAIL;

  useEffect(() => {
    loadUser();
    loadUnreadCount();
  }, []);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      logger.error('Failed to load user:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        withCredentials: true
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      logger.error('Failed to load unread count:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      logger.error('Logout failed:', error);
      toast.error('Logout failed');
    }
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-stone-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - links to main website */}
          <div className="flex items-center space-x-2 cursor-pointer flex-shrink-0 mr-8" onClick={() => navigate('/')}>
            <img 
              src="/logo-transparent.png" 
              alt="Even Rounds Logo" 
              className="h-10 w-10 object-contain"
            />
            <h1 className="text-2xl font-heading font-extrabold text-foreground tracking-tight">Even Rounds</h1>
          </div>

          {/* Yarn Stash Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <button
              data-testid="nav-dashboard-link"
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-stone-600 hover:text-primary font-medium transition-colors"
            >
              <LayoutDashboard className="h-5 w-5" />
              <span>{t('nav.dashboard')}</span>
            </button>
            <button
              data-testid="nav-inventory-link"
              onClick={() => navigate('/inventory')}
              className="flex items-center space-x-2 text-stone-600 hover:text-primary font-medium transition-colors"
            >
              <Package2 className="h-5 w-5" />
              <span>{t('nav.inventory')}</span>
            </button>
            <button
              data-testid="nav-projects-link"
              onClick={() => navigate('/projects')}
              className="flex items-center space-x-2 text-stone-600 hover:text-primary font-medium transition-colors"
            >
              <FolderKanban className="h-5 w-5" />
              <span>{t('nav.projects')}</span>
            </button>
            <button
              data-testid="nav-patterns-link"
              onClick={() => navigate('/patterns')}
              className="flex items-center space-x-2 text-stone-600 hover:text-primary font-medium transition-colors"
            >
              <BookOpen className="h-5 w-5" />
              <span>{t('nav.patterns')}</span>
            </button>
            <button
              data-testid="nav-marketplace-link"
              onClick={() => navigate('/marketplace')}
              className="flex items-center space-x-2 text-stone-600 hover:text-primary font-medium transition-colors"
            >
              <Store className="h-5 w-5" />
              <span>{t('nav.marketplace')}</span>
            </button>
            
            {/* Separator */}
            <div className="h-6 w-px bg-stone-200" />
            
            {/* Website Content Links (separate from Yarn Stash) */}
            <button
              data-testid="nav-designs-link"
              onClick={() => navigate('/designs')}
              className="flex items-center space-x-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              <Sparkles className="h-5 w-5" />
              <span>{t('nav.designs')}</span>
            </button>
            <button
              data-testid="nav-blog-link"
              onClick={() => navigate('/blog')}
              className="flex items-center space-x-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span>{t('nav.blog')}</span>
            </button>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            <button
              data-testid="nav-messages-link"
              onClick={() => navigate('/messages')}
              className="relative flex items-center space-x-2 text-stone-600 hover:text-primary font-medium transition-colors"
            >
              <Mail className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                  {unreadCount}
                </span>
              )}
            </button>
            {user && (
              <div className="flex items-center space-x-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                      <img
                        src={user.picture || 'https://via.placeholder.com/40'}
                        alt={user.name}
                        className="h-10 w-10 rounded-full border-2 border-stone-100"
                      />
                      <span className="text-sm font-medium text-stone-700">{user.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {isOwner && (
                      <>
                        <DropdownMenuItem
                          onClick={() => navigate('/admin/designs')}
                          className="cursor-pointer"
                        >
                          <Settings className="h-4 w-4 mr-2 text-primary" />
                          Design Management
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/admin/blog')}
                          className="cursor-pointer"
                        >
                          <FileText className="h-4 w-4 mr-2 text-primary" />
                          Blog Management
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/admin/analytics')}
                          className="cursor-pointer"
                        >
                          <BarChart3 className="h-4 w-4 mr-2 text-primary" />
                          Analytics Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={() => navigate('/')}
                      className="cursor-pointer"
                    >
                      <Package2 className="h-4 w-4 mr-2 text-primary" />
                      Back to Website
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/support')}
                      className="cursor-pointer"
                    >
                      <Heart className="h-4 w-4 mr-2 text-primary" />
                      Support Even Rounds
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer text-destructive"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            <Button
              data-testid="nav-logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium hidden"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('nav.logout')}
            </Button>
          </div>

          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-stone-600" />
            ) : (
              <Menu className="h-6 w-6 text-stone-600" />
            )}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-stone-100 bg-white">
          <div className="px-4 py-4 space-y-4">
            {/* Yarn Stash Navigation */}
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Yarn Stash</div>
            <button
              onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
            >
              <LayoutDashboard className="h-5 w-5" />
              <span>Dashboard</span>
            </button>
            <button
              onClick={() => { navigate('/inventory'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
            >
              <Package2 className="h-5 w-5" />
              <span>Inventory</span>
            </button>
            <button
              onClick={() => { navigate('/projects'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
            >
              <FolderKanban className="h-5 w-5" />
              <span>Projects</span>
            </button>
            <button
              onClick={() => { navigate('/patterns'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
            >
              <BookOpen className="h-5 w-5" />
              <span>Patterns</span>
            </button>
            <button
              onClick={() => { navigate('/marketplace'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
            >
              <Store className="h-5 w-5" />
              <span>Marketplace</span>
            </button>
            
            {/* Website Content Section */}
            <div className="border-t border-stone-100 pt-4 mt-4">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Website</div>
              <button
                onClick={() => { navigate('/designs'); setMobileMenuOpen(false); }}
                className="w-full flex items-center space-x-2 text-primary hover:text-primary/80 font-medium py-2"
              >
                <Sparkles className="h-5 w-5" />
                <span>Designs</span>
              </button>
              <button
                onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }}
                className="w-full flex items-center space-x-2 text-primary hover:text-primary/80 font-medium py-2"
              >
                <FileText className="h-5 w-5" />
                <span>Blog</span>
              </button>
            </div>
            
            {/* Admin Section - Only visible to owner */}
            {isOwner && (
              <div className="border-t border-stone-100 pt-4 mt-4">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Admin Workspace</div>
                <button
                  onClick={() => { navigate('/admin/designs'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
                >
                  <Settings className="h-5 w-5" />
                  <span>Design Management</span>
                </button>
                <button
                  onClick={() => { navigate('/admin/blog'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
                >
                  <FileText className="h-5 w-5" />
                  <span>Blog Management</span>
                </button>
                <button
                  onClick={() => { navigate('/admin/analytics'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
                >
                  <BarChart3 className="h-5 w-5" />
                  <span>Analytics Dashboard</span>
                </button>
                <button
                  onClick={() => { navigate('/admin/translations'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
                >
                  <Globe className="h-5 w-5" />
                  <span>Translations</span>
                </button>
                <button
                  onClick={() => { navigate('/admin/legal'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
                >
                  <FileText className="h-5 w-5" />
                  <span>Legal Pages</span>
                </button>
              </div>
            )}
            
            {user && (
              <div className="border-t border-stone-100 pt-4 mt-4">
                <div className="flex items-center space-x-3 py-2">
                  <img
                    src={user.picture || 'https://via.placeholder.com/40'}
                    alt={user.name}
                    className="h-10 w-10 rounded-full border-2 border-stone-100"
                  />
                  <span className="text-sm font-medium text-stone-700">{user.name}</span>
                </div>
                <button
                  onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center space-x-2 text-stone-600 hover:text-primary font-medium py-2"
                >
                  <Settings className="h-5 w-5" />
                  <span>Profile Settings</span>
                </button>
              </div>
            )}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium mt-2"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
